/**
 * TeamsContext - Isolated Teams Data Management
 *
 * Provides teams-related data in isolation to prevent unnecessary re-renders
 * when other tournament data (matches, players, locations) changes.
 *
 * Data Sources:
 * - Teams: Real-time subscriptions (onSnapshot) for instant tower position updates
 * - CurrentUserTeam: Derived from teams + user auth state
 *
 * Performance Optimizations:
 * - Page Visibility API: Pauses real-time listeners when tab is hidden
 * - Isolated context: Components using only teams won't re-render on match/player updates
 */

"use client";

import { useTournamentTeams } from "@/hooks";
import { Team } from "@/types";
import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "./AuthContext";

interface TeamsContextType {
  teams: Team[];
  allTeams: Team[];
  currentUserTeam: Team | null;
  loading: boolean;
  isPending: boolean;
  error: string | null;
}

const TeamsContext = createContext<TeamsContextType | undefined>(undefined);

export const useTeamsContext = () => {
  const context = useContext(TeamsContext);
  if (!context) {
    throw new Error("useTeamsContext must be used within a TeamsProvider");
  }
  return context;
};

export function TeamsProvider({
  children,
  tournamentId,
}: {
  children: React.ReactNode;
  tournamentId: string;
}) {
  const { user } = useAuth();

  // ===========================================================================
  // REAL-TIME TEAMS DATA
  // ===========================================================================
  // Fetch teams with real-time updates using onSnapshot
  // Teams are critical for tower display and should update instantly
  // ===========================================================================

  const {
    data: teams = [],
    isLoading: teamsLoading,
    isPending: teamsPending,
    error: teamsError,
  } = useTournamentTeams(tournamentId);

  // Filter out pool teams (not yet placed in tower) for public views
  const placedTeams = useMemo(
    () => teams.filter((t: Team) => t.isPlacedInTower !== false),
    [teams],
  );

  // currentUserTeam checks all teams (including pool) so user can see their team
  const currentUserTeam = useMemo(() => {
    if (!user) return null;

    return teams.find((team: Team) => team.playerIds.includes(user.id)) || null;
  }, [teams, user]);

  const value = useMemo<TeamsContextType>(
    () => ({
      teams: placedTeams,
      allTeams: teams,
      currentUserTeam,
      loading: teamsLoading,
      isPending: teamsPending,
      error: teamsError?.message || null,
    }),
    [placedTeams, teams, currentUserTeam, teamsLoading, teamsPending, teamsError],
  );

  return (
    <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>
  );
}
